import { useEffect, useRef } from 'react';

// First-party, anonymous site analytics. Skips admins (admin-token) and the editor preview iframe.
const API = process.env.REACT_APP_BACKEND_URL;
const VID_KEY = 'demarre-vid';
const SRC_KEY = 'demarre-src';
let queue = [];
let timer = null;

const isAdmin = () => { try { return !!localStorage.getItem('admin-token'); } catch { return false; } };
const inPreview = () => { try { return window.self !== window.top || sessionStorage.getItem('demarre-layout-preview') === '1'; } catch { return true; } };
const enabled = () => typeof window !== 'undefined' && !isAdmin() && !inPreview();

const visitorId = () => {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) { v = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem(VID_KEY, v); }
    return v;
  } catch { return 'anonymous'; }
};

const detectSource = () => {
  const utm = new URLSearchParams(window.location.search).get('utm_source');
  if (utm) return utm.toLowerCase();
  const ref = document.referrer || '';
  if (!ref || ref.includes(window.location.host)) return 'direto';
  const host = (() => { try { return new URL(ref).hostname; } catch { return ''; } })();
  const map = [['google', 'google'], ['instagram', 'instagram'], ['facebook', 'facebook'], ['fb.', 'facebook'], ['whatsapp', 'whatsapp'], ['wa.me', 'whatsapp'],
    ['youtube', 'youtube'], ['bing', 'bing'], ['tiktok', 'tiktok'], ['t.co', 'twitter'], ['linkedin', 'linkedin']];
  const hit = map.find(([k]) => host.includes(k));
  return hit ? hit[1] : (host.replace(/^www\./, '').slice(0, 30) || 'outros');
};
const LAST_SRC_KEY = 'demarre-last-src';
const ATTRIBUTION_DAYS = 30;
const source = () => {
  try {
    let s = sessionStorage.getItem(SRC_KEY);
    if (!s) {
      s = detectSource(); sessionStorage.setItem(SRC_KEY, s);
      if (s !== 'direto') localStorage.setItem(LAST_SRC_KEY, JSON.stringify({ s, at: Date.now() }));
    }
    return s;
  } catch { return 'direto'; }
};

// Booking origin: this visit's source, else the last non-direct source seen in the past 30 days.
export const bookingSource = () => {
  const s = source();
  if (s !== 'direto') return s;
  try {
    const last = JSON.parse(localStorage.getItem(LAST_SRC_KEY) || 'null');
    if (last && Date.now() - last.at < ATTRIBUTION_DAYS * 86400000) return last.s;
  } catch { /* ignore */ }
  return 'direto';
};
const device = () => (window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop');
const lang = () => { try { return localStorage.getItem('demarre-language') || ''; } catch { return ''; } };

export const pageKeyOf = (path) => {
  if (path === '/') return 'home';
  if (path.startsWith('/pilgrimages')) return 'pilgrimages';
  if (path.startsWith('/tour/')) return 'tour';
  if (path.startsWith('/faqs') || path === '/faq') return 'faq';
  if (path.startsWith('/lead-a-group')) return 'lead_group';
  if (path.startsWith('/galeria')) return 'gallery';
  if (path.startsWith('/booking')) return 'booking';
  if (path.startsWith('/about')) return 'about';
  if (path.startsWith('/comparar')) return 'compare';
  if (path.startsWith('/login')) return 'login';
  if (path.startsWith('/minhas-viagens') || path.startsWith('/my-bookings')) return 'client_area';
  return 'other';
};

const flush = (beacon = false) => {
  clearTimeout(timer); timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, 25);
  const body = JSON.stringify({ vid: visitorId(), lang: ['pt', 'en', 'es'].includes(lang()) ? lang() : '', device: device(), source: source(), events });
  try {
    if (beacon && navigator.sendBeacon) navigator.sendBeacon(`${API}/api/track`, new Blob([body], { type: 'application/json' }));
    else fetch(`${API}/api/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* analytics must never break the site */ }
  if (queue.length) flush(beacon);
};

const recent = new Map();
const ONCE = ['page_view', 'tour_view', 'booking_start'];

export const track = (type, { target = '', tourId = '' } = {}) => {
  if (!enabled()) return;
  const path = window.location.pathname;
  // Drop duplicates fired twice in a row (React StrictMode / double effects).
  if (ONCE.includes(type)) {
    const k = `${type}|${target}|${tourId}|${path}`;
    const now = Date.now();
    if (now - (recent.get(k) || 0) < 1500) return;
    recent.set(k, now);
  }
  queue.push({ type, target: String(target).slice(0, 80), tour_id: String(tourId || '').slice(0, 40), page: pageKeyOf(path), path: path.slice(0, 200) });
  if (queue.length >= 20) flush(); else if (!timer) timer = setTimeout(flush, 3000);
};

if (typeof window !== 'undefined') {
  source(); // capture UTM/referrer on landing, before SPA navigation drops the query string
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
  window.addEventListener('pagehide', () => flush(true));
}

// Global click delegation: menu, main CTAs, WhatsApp, brochure, tour links, [data-track="type:target"].
const tourOfPath = (p) => (p.startsWith('/tour/') ? p.split('/')[2] : '');
export const handleTrackedClick = (e) => {
  const el = e.target.closest?.('a, button, [data-track]');
  if (!el) return;
  const tid = el.getAttribute('data-testid') || '';
  const href = el.getAttribute('href') || '';
  const here = tourOfPath(window.location.pathname);
  const custom = el.closest('[data-track]')?.getAttribute('data-track');
  if (custom) { const [type, target] = custom.split(':'); track(type, { target, tourId: here }); return; }
  if (/^(mobile-)?navbar-link-/.test(tid)) return track('nav_click', { target: tid.replace(/^(mobile-)?navbar-link-/, '') });
  if (/navbar-lead-cta$/.test(tid)) return track('cta_click', { target: 'lead_group' });
  if (tid === 'client-area-btn' || tid === 'mobile-client-area-btn') return track('cta_click', { target: 'client_area' });
  if (tid === 'book-now-btn' || tid === 'hold-24h-near-book') return track('book_click', { target: tid === 'book-now-btn' ? 'book' : 'hold_24h', tourId: here });
  if (/wa\.me|whatsapp\.com/i.test(href)) return track('whatsapp_click', { target: pageKeyOf(window.location.pathname), tourId: here });
  if (/brochure|brochura/i.test(href + tid)) return track('brochure_click', { target: pageKeyOf(window.location.pathname), tourId: here });
  if (href.startsWith('/lead-a-group')) return track('cta_click', { target: 'lead_group' });
  const m = href.match(/^\/tour\/([^/?#]+)/);
  if (m && m[1] !== here) return track('tour_click', { target: pageKeyOf(window.location.pathname), tourId: m[1] });
  return undefined;
};

// Tour cards/rows: one impression when ≥50% visible + click (capture phase, before inner stopPropagation).
export const useTourTracking = (tourId) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !tourId || !enabled() || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { track('tour_impression', { tourId, target: pageKeyOf(window.location.pathname) }); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [tourId]);
  return { ref, onClickCapture: () => track('tour_click', { tourId, target: pageKeyOf(window.location.pathname) }) };
};
